import { Handshake, PackageSearch, ShieldCheck } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { HexNut, Wordmark } from '~/components/brand'
import { LanguageSwitch } from '~/components/language-switch'
import { ThemeSwitch } from '~/components/theme-switch'
import type { MessageKey } from '~/i18n'
import { useT } from '~/lib/i18n'

const PITCH: {
  icon: ComponentType<{ className?: string }>
  title: MessageKey
  body: MessageKey
}[] = [
  {
    icon: PackageSearch,
    title: 'auth.pitch.one_list',
    body: 'auth.pitch.one_list_body',
  },
  {
    icon: Handshake,
    title: 'auth.pitch.agree',
    body: 'auth.pitch.agree_body',
  },
  {
    icon: ShieldCheck,
    title: 'auth.pitch.recorded',
    body: 'auth.pitch.recorded_body',
  },
]

/**
 * Signed-out screens get the full brand treatment: a workshop panel on the left at
 * desktop widths, the form on the right. On a phone the panel collapses to a header
 * strip, because a contractor signing in on site wants the keyboard, not a pitch.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  const t = useT()
  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      {/*
       * The panel is always dark, in either theme — amber on graphite is the brand,
       * and a signed-out screen is the one place to state it without compromise.
       * Scoping `data-theme` re-points every token underneath; no extra CSS.
       */}
      <aside
        data-theme="dark"
        className="glow relative isolate hidden overflow-hidden bg-canvas text-ink lg:flex lg:w-[46%] lg:max-w-2xl lg:flex-col lg:justify-between lg:p-12"
      >
        <div className="blueprint absolute inset-0 -z-10" aria-hidden />
        {/* Oversized mark, bled off the corner like a stencil on a crate. */}
        <HexNut className="pointer-events-none absolute -right-32 -bottom-36 -z-10 size-[34rem] opacity-[0.05]" />

        <div className="relative">
          <div className="flex items-center gap-3">
            <HexNut className="size-10" />
            <Wordmark className="text-lg" />
          </div>
        </div>

        <div className="relative max-w-md">
          <p className="stencil text-primary">{t('auth_layout.wholesale_hardware_brokered')}</p>
          <h2 className="mt-4 text-4xl leading-[1.1] font-extrabold text-balance">
            {t('auth_layout.between_the_shop_and_the_site')}
          </h2>
          <ul className="mt-9 space-y-6">
            {PITCH.map(({ icon: Icon, title: heading, body }) => (
              <li key={heading} className="flex gap-4">
                <span className="hex grid size-10 shrink-0 place-items-center bg-primary-soft text-primary">
                  <Icon className="size-[1.15rem]" aria-hidden />
                </span>
                <div>
                  <p className="text-sm font-bold text-ink">{t(heading)}</p>
                  <p className="mt-1 text-sm text-ink-muted">{t(body)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex items-end justify-between gap-4">
          <p className="stencil text-ink-subtle">
            {t('auth_layout.accounts_are_issued_by_the_office')}
          </p>
          <div className="hazard h-1.5 w-28 rounded-full opacity-70" aria-hidden />
        </div>
      </aside>

      <div className="blueprint flex flex-1 flex-col">
        <header className="flex items-center justify-between px-4 py-4 sm:px-6 lg:justify-end">
          <div className="flex items-center gap-2.5 lg:hidden">
            <HexNut className="size-8" />
            <Wordmark className="text-[0.9375rem]" />
          </div>
          <div className="flex items-center gap-2">
            {/* Before anybody has an account to remember it against. */}
            <LanguageSwitch className="w-32" />
            <ThemeSwitch className="w-28" />
          </div>
        </header>

        <div className="flex flex-1 items-center justify-center px-4 pt-2 pb-12 sm:px-6">
          <div className="w-full max-w-[24rem] animate-rise">
            <div className="mb-7">
              <h1 className="text-2xl font-extrabold text-ink sm:text-[1.75rem]">{title}</h1>
              {subtitle ? <p className="mt-2 text-sm text-ink-muted">{subtitle}</p> : null}
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
